import { yupResolver } from '@hookform/resolvers/yup';
import { Button, InputAdornment } from '@mui/material';
import SimpleDialog from 'core/components/Dialogs/SimpleDialog';
import { getFullName } from 'core/utils/commonUtils';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useMemo } from 'react';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import StoreContext from 'stores/context/StoreContext';
import { number, object, string } from 'yup';
import type { ProjectProductMarkup } from 'models/order/orderProject';
import type { FC } from 'react';
import type { Schema } from 'yup';

type FormData = Pick<ProjectProductMarkup, 'amountPerItem' | 'remark'>;

const schema: Schema<FormData> = object().shape({
  remark: string().ensure(),
  amountPerItem: number().min(0).default(0).max(1_000_000_000),
});

const MarkupEditDialog: FC<MarkupEditDialogProps> = observer((props) => {
  const { markup, open, onCancel, onSave } = props;

  const { userStore } = useContext(StoreContext);

  const formContext = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const defaultMarkup = useMemo(
    () =>
      markup ?? {
        id: 0,
        userId: userStore.userData?.id ?? 0,
        remark: '',
        amountPerItem: 0,
        dateCreate: new Date(),
        user: {
          id: userStore.userData?.id ?? 0,
          fullName: isNil(userStore.userData) ? '' : getFullName(userStore.userData),
        },
      },
    [markup, userStore.userData]
  );

  useEffect(() => {
    if (open) formContext.reset(defaultMarkup);
  }, [defaultMarkup, formContext, open]);

  const handleOnSave: VoidFunction = () => {
    const formValue = formContext.getValues();

    const editedMarkup = {
      ...defaultMarkup,
      ...formValue,
    };

    !isNil(onSave) && onSave(editedMarkup);
  };

  return (
    <SimpleDialog
      title={'Наценка'}
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth={'sm'}
      actions={
        <>
          <Button
            onClick={() => {
              void formContext.handleSubmit(handleOnSave)();
            }}>
            Сохранить
          </Button>
          <Button onClick={onCancel}>Закрыть</Button>
        </>
      }
      content={
        <FormContainer formContext={formContext} onSuccess={handleOnSave}>
          <TextFieldElement
            name={`amountPerItem`}
            type='number'
            required
            fullWidth
            label='Наценка на единицу товара'
            margin='normal'
            inputProps={{ min: 0 }}
            InputProps={{
              endAdornment: <InputAdornment position='end'>₽</InputAdornment>,
            }}
          />

          <TextFieldElement
            fullWidth
            name={`remark`}
            label='Примечание'
            margin='normal'
          />
        </FormContainer>
      }
    />
  );
});

export interface MarkupEditDialogProps {
  markup?: ProjectProductMarkup;
  open: boolean;
  onSave?: (markup: ProjectProductMarkup) => void;
  onCancel?: VoidFunction;
}

export default MarkupEditDialog;
